import {
  ArrowPathRoundedSquareIcon,
  ArrowUpTrayIcon,
  EnvelopeIcon,
  LinkIcon,
  NewspaperIcon,
} from "@heroicons/react/24/outline";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";

import { AppContext } from "@src/App";
import create from "@src/api/chats/multicast/messages/create";
import forYou from "@src/api/contacts/forYou";
import repost from "@src/api/posts/repost";
import Button from "@src/components/elements/input/Button";
import Input from "@src/components/elements/input/Input";
import SharePost from "@src/components/elements/posts/form/SharePost";
import ModalWrapper from "@src/components/elements/shared/ModalWrapper";
import ShareUser from "@src/components/popup/ShareUser";
import ShareController from "@src/components/popup/share/ShareController";
import { useAppSelector } from "@src/state/hooks";
import { MediaType } from "@src/types/MediaType";
import { UserType } from "@src/types/UserType";

type ContentType = {
  title?: string;
  body?: string;
  link?: string;
  media?: Array<MediaType>;
};

type Props = {
  onClose: () => void;
  postId?: string;
  content: ContentType;
  isMobile?: boolean;
};

const SharePopup = ({ onClose, postId, content, isMobile }: Props) => {
  const { user } = useAppSelector(state => state.user);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);
  const [selectedService, setSelectedService] = useState<string>("messenger");
  const [messageBody, setMessageBody] = useState<string>("");
  const appContext = useContext(AppContext);
  const [servicesVisible, setServicesVisible] = useState(true);

  if (appContext?.setFullscreen && isMobile) appContext.setFullscreen(true);

  const repostMutation = useMutation({
    mutationKey: ["post"],
    mutationFn: () => repost({ id: `${postId}` }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      onClose();
    },
    onError: () => {
      console.error("Cannot create post");
    },
  });

  const copyLink = async (link: string) => {
    onClose();
    try {
      await navigator.clipboard.writeText(link);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const forYouResult = useInfiniteQuery({
    queryKey: ["contacts", "for-you", searchQuery],
    queryFn: ({ pageParam }) => forYou({ params: { page: pageParam, q: searchQuery } }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const nextPage = lastPage.current_page + 1;
      return nextPage <= lastPage.total_pages ? nextPage : null;
    },
  });

  const emailData = "?subject=" + content.title + "&body=" + content.body;

  const services = [
    {
      label: t("buttons.share"),
      type: "external",
      icon: (
        <div className="size-6">
          <ShareController shareData={{ title: content.title, text: content.body, url: "" }}>
            <ArrowUpTrayIcon className="size-6" />
          </ShareController>
        </div>
      ),
    },
    {
      label: t("main.panel.messenger"),
      type: "messenger",
      icon: <EnvelopeIcon className="size-6" />,
    },
    {
      label: t("buttons.quote"),
      type: "newsfeed",
      icon: <NewspaperIcon className="size-6" />,
      onClick: () => {
        setServicesVisible(false);
      },
    },
    {
      label: "E-Mail",
      type: t("main.navLinks.jobApplicationForm.email"),
      icon: <EnvelopeIcon className="size-6" />,
      onClick: () => {
        window.location.href = "mailto:" + emailData;
        onClose();
      },
    },
    {
      label: t("main.interactionNotifications.tabs.repost"),
      type: "repost",
      icon: <ArrowPathRoundedSquareIcon className="size-6" />,
      onClick: () => {
        repostMutation.mutate();
        onClose();
      },
    },
    {
      label: t("components.shared.copyLink"),
      type: "copy",
      icon: <LinkIcon className="size-6" />,
      onClick: () => {
        copyLink(content.body ?? "");
      },
    },
  ];

  const createMutation = useMutation({
    mutationKey: ["chats", "messages", "multicast", "create"],
    mutationFn: create,
    onSuccess: () => {
      console.log("success");
    },
    onError: () => {
      alert("Failed");
    },
  });

  if (!user) return <></>;

  return (
    <ModalWrapper
      onCancel={() => {
        if (appContext?.setBottomNavHidden && isMobile) appContext.setBottomNavHidden(false);
        onClose();
      }}
      hideActions={true}
      open={true}
    >
      {selectedService === "messenger" && (
        <div className="flex h-[calc(85vh-100px)] flex-col gap-2">
          <Input
            name="search-users"
            placeholder={t("main.browsePlaceholder")}
            onChange={e => setSearchQuery(e)}
            clearable
          />
          <div className="flex flex-col overflow-y-auto pr-4 scrollbar-thin">
            {forYouResult.data && (
              <>
                {forYouResult.data.pages.map(page =>
                  page.data.map((item: UserType) => (
                    <div key={item.id}>
                      <ShareUser
                        user={item}
                        onChange={checked => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, item]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(user => user.id !== item.id));
                          }
                        }}
                        checked={selectedUsers.includes(item)}
                      />
                    </div>
                  )),
                )}
              </>
            )}
          </div>
        </div>
      )}

      {selectedService === "newsfeed" && (
        <div className="overflow-y-auto scrollbar-thin max-lg:h-[calc(100vh-125px)] lg:max-h-[500px]">
          <SharePost
            onSuccess={() => onClose()}
            messageText={content.body}
            mediaList={content.media}
          />
        </div>
      )}

      {!forYouResult.data && forYouResult.isPending && (
        <div className="mx-auto size-8 overflow-hidden">
          <svg
            aria-hidden="true"
            className="size-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      )}

      {selectedUsers.length === 0 && servicesVisible && (
        <div className="grid h-[71px] grid-flow-col items-center overflow-x-scroll scrollbar-none lg:hover:overflow-x-auto">
          {services.map(service => {
            return (
              <button
                className="flex w-[77px] flex-col items-center text-sm"
                key={service.type}
                onClick={() => {
                  setSelectedService(service.type);
                  if (service.onClick) service.onClick();
                }}
              >
                <div
                  className={`rounded-full p-2 ${selectedService === service.type ? "bg-blue-200" : "bg-gray-200"}`}
                >
                  {service.icon}
                </div>
                <div className="whitespace-nowrap text-xs">{service.label}</div>
              </button>
            );
          })}
        </div>
      )}
      {selectedUsers.length > 0 && (
        <div className="flex min-h-[77px] w-full flex-row overflow-y-hidden border-t-2 border-gray-200 px-1 pt-1">
          <div className="flex w-full flex-col">
            <TextareaAutosize
              value={messageBody}
              minRows={1}
              maxRows={5}
              onChange={e => setMessageBody(e.target.value)}
              className="block w-full break-all rounded-xl border-0 bg-gray-100 text-sm"
            />
            <Button
              variant={Button.Variants.Transparent}
              onClick={() => {
                switch (selectedService) {
                  case "messenger":
                    createMutation.mutate({
                      data: {
                        link_preview_id: content.link,
                        messageBody,
                        media: content?.media?.map((m: MediaType) => m.id) ?? [],
                      },
                    });
                    break;
                  case "external":
                    break;
                  case "email":
                    break;
                  case "repost":
                    break;
                  case "copy":
                    break;
                }
              }}
            >
              {t("buttons.send")}
            </Button>
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default SharePopup;
